import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useToast } from "../hooks/useToast";
import { getVehicleById, createWriteOff, getWriteOffsByVehicle } from "../services/api";

interface WriteOff {
  id: number;
  amount: number;
  reason: string;
  createdAt: string;
}

const CreateWriteOffPageImproved: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<any>(null);
  const [amount, setAmount] = useState<number | "">("");
  const [reason, setReason] = useState("");
  const [lastWriteOffs, setLastWriteOffs] = useState<WriteOff[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { showToast, ToastContainer } = useToast();

  const fetchVehicle = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const data = await getVehicleById(id!);
      setVehicle(data);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch vehicle");
      showToast(err?.message || "Failed to fetch vehicle", "error");
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  const fetchWriteOffs = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const vehicleIdNum = Number(id);
      let data = await getWriteOffsByVehicle(vehicleIdNum);
      let writeOffs = data.sort((a: WriteOff, b: WriteOff) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setLastWriteOffs(writeOffs.slice(0, 5));
    } catch (err: any) {
      setError(err?.message || "Failed to fetch write-offs");
      showToast(err?.message || "Failed to fetch write-offs", "error");
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    fetchVehicle();
    fetchWriteOffs();
    // eslint-disable-next-line
  }, [fetchVehicle, fetchWriteOffs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !reason) {
      setError("Please enter both amount and reason");
      showToast("Please enter both amount and reason", "warning");
      return;
    }
    if (!vehicle) return;
    if (!window.confirm(`Confirm write-off of ₹${amount} for vehicle ${vehicle.vehicleNo}?`)) return;
    setLoading(true);
    setError("");
    try {
      await createWriteOff({ vehicleId: vehicle.id, amount: Number(amount), reason });
      showToast("Write-off recorded successfully!", "success");
      setAmount("");
      setReason("");
      fetchWriteOffs();
      fetchVehicle();
    } catch (err: any) {
      setError(err?.message || "Error creating write-off");
      showToast(err?.message || "Error creating write-off", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!vehicle) {
    return (
      <div className="min-h-screen w-full bg-gray-50 flex flex-col">
        <ToastContainer />
        <Card className="w-full max-w-none mx-auto p-2 md:p-6">
          <div className="flex items-center mb-4">
            <Button color="secondary" onClick={() => navigate(-1)} className="mr-4">Back</Button>
            <h1 className="text-2xl font-bold text-red-700">Create Write-Off</h1>
          </div>
          {loading ? <LoadingSpinner /> : <div className="text-gray-500">Loading vehicle details...</div>}
          {error && <div className="text-red-600 mb-2">{error}</div>}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 flex flex-col">
      <ToastContainer />
      <Card className="w-full max-w-none mx-auto p-2 md:p-6">
        <div className="flex items-center mb-4">
          <Button color="secondary" onClick={() => navigate(-1)} className="mr-4">Back</Button>
          <h1 className="text-2xl font-bold text-red-700">Create Write-Off</h1>
        </div>
        {loading && <LoadingSpinner />}
        {error && <div className="text-red-600 mb-2">{error}</div>}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Vehicle Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <p><span className="font-semibold text-gray-600">Vehicle No:</span> {vehicle.vehicleNo}</p>
            <p><span className="font-semibold text-gray-600">Owner:</span> {vehicle.ownerName}</p>
            <p><span className="font-semibold text-gray-600">Type:</span> {vehicle.vehicleType}</p>
            <p><span className="font-semibold text-gray-600">Balance:</span> ₹{vehicle.balance}</p>
          </div>
        </div>
        <form
          className="bg-white shadow rounded-lg p-6 w-full flex flex-col gap-4 mb-6"
          onSubmit={handleSubmit}
        >
          {error && <p className="text-red-500">{error}</p>}
          <input
            type="number"
            placeholder="Write-Off Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))}
            className="border p-3 rounded w-full focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          <textarea
            placeholder="Reason for Write-Off"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="border p-3 rounded w-full focus:outline-none focus:ring-2 focus:ring-red-400"
            rows={3}
          />
          <Button
            type="submit"
            style={{ background: "#ef4444", color: "#fff" }}
            className={`w-full py-3 rounded-lg text-base font-semibold shadow ${loading ? "bg-gray-400 cursor-not-allowed" : "hover:bg-red-700 transition"}`}
            loading={loading}
            disabled={loading}
          >
            {loading ? "Recording..." : "Record Write-Off"}
          </Button>
        </form>
        <div>
          <h2 className="text-xl font-semibold mb-3">Last 5 Write-Offs</h2>
          {lastWriteOffs.length === 0 ? (
            <p className="text-gray-500">No previous write-offs found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white shadow rounded text-sm md:text-base">
                <thead className="hidden md:table-header-group">
                  <tr className="bg-gray-100 text-left">
                    <th className="py-2 px-4">ID</th>
                    <th className="py-2 px-4">Amount (₹)</th>
                    <th className="py-2 px-4">Reason</th>
                    <th className="py-2 px-4">Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {lastWriteOffs.map((w) => (
                    <tr
                      key={w.id}
                      className="border-b md:table-row flex flex-col md:flex-row mb-4 md:mb-0 p-4 md:p-0 rounded md:rounded-none hover:bg-red-50 transition"
                    >
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">ID: </span>
                        {w.id}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Amount: </span>
                        ₹{w.amount}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Reason: </span>
                        {w.reason}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Date: </span>
                        {new Date(w.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CreateWriteOffPageImproved;
